import { ACCEPTANCE_DEADLINE_SOURCE_LABELS, type Order } from "../types";
import { formatDate } from "@/utils/formatDate";

/**
 * Termin przyjęcia zamówienia „Wysłane” (backend: apps.orders.acceptance_deadline).
 * `confirmation_deadline` to dzień, do którego podwykonawca powinien przyjąć zamówienie;
 * następnego dnia (zadanie o 7:00) nieprzyjęte zamówienie dostaje flagę „długo oczekuje”
 * i trafia do dyrekcji — nic nie jest anulowane automatycznie. Daty liczone jako lokalne
 * dni kalendarzowe.
 */

/** Najdalszy termin, jaki przyjmie backend przy przedłużeniu (dni od dziś). */
export const MAX_EXTENSION_DAYS_AHEAD = 90;

const pad = (n: number) => String(n).padStart(2, "0");

export function isoDay(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function todayIso(): string {
  return isoDay(new Date());
}

/** `yyyy-mm-dd` + n dni (lokalnie, bez przesunięć strefy). */
export function addDaysIso(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  return isoDay(new Date(y, m - 1, d + days));
}

const daysPhrase = (n: number) => `${n} ${n === 1 ? "dzień" : "dni"}`;

export type AcceptanceUrgency = "overdue" | "soon" | "normal";

export interface AcceptanceDeadlineInfo {
  deadline: string;
  /** Dzień, od którego automat oznaczy zamówienie jako długo oczekujące. */
  overdueOn: string;
  urgency: AcceptanceUrgency;
  /** „5 dni od wysłania 21.10.2026, z umowy ramowej, przedłużony” — albo null bez danych. */
  termDetail: string | null;
  tracked: boolean;
  /** Flaga „długo oczekuje” (ISO datetime) albo null. */
  overdueSince: string | null;
}

/** null, gdy zamówienie na nic nie czeka (status inny niż „Wysłane” albo brak terminu). */
export function acceptanceDeadlineInfo(order: Order, today: string = todayIso()): AcceptanceDeadlineInfo | null {
  const deadline = order.confirmation_deadline;
  if (order.status !== "sent" || !deadline) return null;
  const urgency: AcceptanceUrgency =
    deadline < today ? "overdue" : deadline <= addDaysIso(today, 1) ? "soon" : "normal";
  const parts: string[] = [];
  if (order.acceptance_deadline_days) {
    const sent = order.sent_at ? ` od wysłania ${formatDate(order.sent_at)}` : " od wysłania";
    parts.push(`${daysPhrase(order.acceptance_deadline_days)}${sent}`);
    const source = order.acceptance_deadline_source;
    if (source) parts.push(ACCEPTANCE_DEADLINE_SOURCE_LABELS[source]);
  }
  if (order.acceptance_deadline_extended) parts.push("przedłużony");
  return {
    deadline,
    overdueOn: addDaysIso(deadline, 1),
    urgency,
    termDetail: parts.length ? parts.join(", ") : null,
    tracked: Boolean(order.acceptance_deadline_tracked),
    overdueSince: order.acceptance_overdue_since ?? null,
  };
}

/** Najwcześniejszy dzień, na który można przesunąć termin: dzień po obecnym, nie wcześniej niż dziś. */
export function minExtensionDate(deadline: string, today: string = todayIso()): string {
  const next = addDaysIso(deadline, 1);
  return next > today ? next : today;
}
